<template>
  <div class="p-6">
    <!-- 页面标题 -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">Mock 书籍管理</h1>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">管理 Mock 书籍的分类和显示信息</p>
    </div>

    <!-- 筛选器和操作按钮 -->
    <div class="mb-4 flex gap-4">
      <div class="flex-1">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">分类筛选</label>
        <select v-model="selectedCategoryId" @change="filterBooks"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
          <option :value="null">全部分类</option>
          <option v-for="category in categories" :key="category.id" :value="category.id">
            {{ category.displayName }}
          </option>
        </select>
      </div>
      <div class="flex items-end">
        <button @click="handleCreate"
          class="primary-btn gap-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          新增书籍
        </button>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
    </div>

    <!-- 书籍列表 -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900">
      <BookList :books="filteredBooks" @edit="handleEdit" />
    </div>

    <!-- 创建书籍模态框 -->
    <div v-if="showCreateModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-70 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h2 class="text-xl font-bold mb-4 dark:text-gray-100">新增书籍</h2>
        <form @submit.prevent="handleCreateSubmit">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">书籍名称 <span
                  class="text-red-500">*</span></label>
              <input v-model="createForm.name" type="text" required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                placeholder="例如：C19" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">来源 ID <span
                  class="text-red-500">*</span></label>
              <input v-model.number="createForm.sourceId" type="number" required min="1"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                placeholder="例如：19" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">显示名称</label>
              <input v-model="createForm.displayName" type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                placeholder="例如：剑桥雅思 19" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">分类</label>
              <select v-model="createForm.categoryId"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                <option :value="null">未分类</option>
                <option v-for="category in categories" :key="category.id" :value="category.id">
                  {{ category.displayName }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">描述</label>
              <textarea v-model="createForm.description" rows="3"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                placeholder="书籍描述（可选）"></textarea>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">排序顺序</label>
              <input v-model.number="createForm.sortOrder" type="number"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
            </div>
            <div>
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="createForm.isVisible" type="checkbox"
                  class="w-4 h-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500" />
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">可见（用户前端显示）</span>
              </label>
            </div>
          </div>
          <div class="mt-6 flex justify-end space-x-3">
            <button type="button" @click="showCreateModal = false"
              class="cancel-btn">
              取消
            </button>
            <button type="submit" :disabled="creating"
              class="primary-btn disabled:opacity-50">
              {{ creating ? '创建中...' : '创建' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- 编辑书籍模态框 -->
    <div v-if="showEditModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-70 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h2 class="text-xl font-bold mb-4 dark:text-gray-100">编辑书籍</h2>
        <form @submit.prevent="handleUpdate">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">原始名称</label>
              <input :value="editingBook?.name" disabled
                class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">显示名称</label>
              <input v-model="editForm.displayName" type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                placeholder="例如：剑桥雅思 18" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">分类</label>
              <select v-model="editForm.categoryId"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                <option :value="null">未分类</option>
                <option v-for="category in categories" :key="category.id" :value="category.id">
                  {{ category.displayName }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">描述</label>
              <textarea v-model="editForm.description" rows="3"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                placeholder="书籍描述（可选）"></textarea>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">排序顺序</label>
              <input v-model.number="editForm.sortOrder" type="number"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
            </div>
            <div>
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="editForm.isVisible" type="checkbox"
                  class="w-4 h-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500" />
                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">可见（用户前端显示）</span>
              </label>
            </div>
          </div>
          <div class="mt-6 flex justify-end space-x-3">
            <button type="button" @click="showEditModal = false"
              class="cancel-btn">
              取消
            </button>
            <button type="submit" :disabled="updating"
              class="primary-btn disabled:opacity-50">
              {{ updating ? '更新中...' : '更新' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- 错误提示 -->
    <div v-if="errorMessage"
      class="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg dark:bg-red-900/30 dark:border-red-700 dark:text-red-400"
      role="alert">
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clip-rule="evenodd" />
        </svg>
        <span>{{ errorMessage }}</span>
        <button @click="errorMessage = ''" class="ml-2 text-red-700 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </div>

    <!-- 成功提示 -->
    <div v-if="successMessage"
      class="fixed bottom-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg shadow-lg dark:bg-green-900/30 dark:border-green-700 dark:text-green-400"
      role="alert">
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clip-rule="evenodd" />
        </svg>
        <span>{{ successMessage }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import BookList from '@/components/mock/BookList.vue'
import { getAllBooksAPI, createBookAPI, updateBookAPI } from '@/api/mock/book'
import { getAllCategoriesAPI } from '@/api/mock/category'
import type { Book, CreateBookRequest, UpdateBookRequest } from '@/types/mock/book'
import type { Category } from '@/types/mock/category'

// 书籍列表
const books = ref<Book[]>([])
const categories = ref<Category[]>([])

// 筛选条件
const selectedCategoryId = ref<number | null>(null)

// 加载状态
const loading = ref(false)
const creating = ref(false)
const updating = ref(false)

// 模态框状态
const showCreateModal = ref(false)
const showEditModal = ref(false)

// 创建表单数据
const createForm = ref<CreateBookRequest>({
  name: '',
  sourceId: 1,
  displayName: null,
  description: null,
  categoryId: null,
  sortOrder: 0,
  isVisible: true,
})

// 编辑表单数据
const editForm = ref<UpdateBookRequest>({
  displayName: null,
  description: null,
  categoryId: null,
  sortOrder: null,
  isVisible: true,  // ✅ 默认为 true（可见）
})

const editingBook = ref<Book | null>(null)

// 消息提示
const errorMessage = ref('')
const successMessage = ref('')

/**
 * 筛选后的书籍列表
 */
const filteredBooks = computed(() => {
  if (selectedCategoryId.value === null) {
    return books.value
  }
  return books.value.filter((book) => book.categoryId === selectedCategoryId.value)
})

/**
 * 加载所有书籍
 */
const loadBooks = async () => {
  loading.value = true
  errorMessage.value = ''
  try {
    const response = await getAllBooksAPI()
    books.value = response.data || []
  } catch (error) {
    console.error('加载书籍失败:', error)
    errorMessage.value = '加载书籍失败，请刷新页面重试'
  } finally {
    loading.value = false
  }
}

/**
 * 加载所有分类
 */
const loadCategories = async () => {
  try {
    const response = await getAllCategoriesAPI()
    categories.value = (response.data || []).filter((cat) => cat.isActive)
  } catch (error) {
    console.error('加载分类失败:', error)
  }
}

/**
 * 筛选书籍
 */
const filterBooks = () => {
  // 筛选逻辑由 computed 属性处理
}

/**
 * 处理创建书籍
 */
const handleCreate = () => {
  // 重置表单
  createForm.value = {
    name: '',
    sourceId: 1,
    displayName: null,
    description: null,
    categoryId: null,
    sortOrder: 0,
    isVisible: true,
  }
  showCreateModal.value = true
}

/**
 * 处理创建书籍提交
 */
const handleCreateSubmit = async () => {
  creating.value = true
  errorMessage.value = ''
  try {
    await createBookAPI(createForm.value)
    successMessage.value = '创建书籍成功'
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)
    showCreateModal.value = false
    await loadBooks()
  } catch (error: any) {
    console.error('创建书籍失败:', error)
    errorMessage.value = error.response?.data?.message || '创建书籍失败，请重试'
  } finally {
    creating.value = false
  }
}

/**
 * 处理编辑书籍
 */
const handleEdit = (book: Book) => {
  editingBook.value = book
  editForm.value = {
    displayName: book.displayName,
    description: book.description,
    categoryId: book.categoryId,
    sortOrder: book.sortOrder,
    isVisible: book.isVisible ?? true,  // ✅ 确保是布尔值，默认为 true
  }
  showEditModal.value = true
}

/**
 * 处理更新书籍
 */
const handleUpdate = async () => {
  if (!editingBook.value) return

  updating.value = true
  errorMessage.value = ''
  try {
    await updateBookAPI(editingBook.value.id, editForm.value)
    successMessage.value = '更新书籍成功'
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)
    showEditModal.value = false
    editingBook.value = null
    await loadBooks()
  } catch (error: any) {
    console.error('更新书籍失败:', error)
    errorMessage.value = error.response?.data?.message || '更新书籍失败，请重试'
  } finally {
    updating.value = false
  }
}

/**
 * 组件挂载时加载数据
 */
onMounted(async () => {
  await Promise.all([loadBooks(), loadCategories()])
})
</script>
